/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import AGB from '../AGB';
import VoucherDetail from './VoucherDetail';
import GenericDialog from '../GenericDialog';
import ContentBox from '../layout/ContentBox';
import ApiClient from '../../loopback/apiClient';
import { VoucherCode, Trial } from '../../redux/types';
import { showErrorNotification, setLoading } from '../../redux/actions';
import TermsConfirmationDialog from '../trial/TermsConfirmationDialog';
import { downloadContainerUrl } from '../../utilities/urlHelper';
import { TERMS_CONTAINER_NAME } from '../../environment';

type Props = {
  voucherDetail: VoucherCode & { trial: Trial };
  userId: number;
  locale: string;
};

export type VoucherEnrollmentType = Props;

const VoucherEnrollment: React.FC<Props> = ({ voucherDetail, userId, locale }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [agbAccept, setAgbAccept] = useState<boolean>(false);
  const [agbAcceptError, setAgbAcceptError] = useState<boolean>(false);
  const [agbDialog, showAGBDialog] = useState<boolean>(false);
  const [showTerms, setShowTerms] = useState<boolean>(!!voucherDetail.trial.terms);
  const termsUrl = voucherDetail.trial.terms ? downloadContainerUrl(TERMS_CONTAINER_NAME, voucherDetail.trial.terms) : '';

  const handleChangeTerms = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgbAccept(e.target.checked);
  };
  const enrollHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (!agbAccept) {
      setAgbAcceptError(true);
      return;
    }
    dispatch(setLoading(true));
    ApiClient.enrollToTrial(voucherDetail.trial)
      .then(() => {
        const voucherData: VoucherCode = {
          code: voucherDetail.code,
          id: voucherDetail.id,
          trialId: voucherDetail.trialId,
          quipUserId: userId,
        };
        return ApiClient.updateClaimData(voucherData);
      })
      .then(() => {
        navigate('/enrollments');
      })
      .catch((err) => {
        switch (err.message) {
          case 'TRIAL_FULL':
            dispatch(showErrorNotification(<FormattedMessage id="voucher.trialFull" />));
            break;
          case 'ALREADY_ENROLLED':
            dispatch(showErrorNotification(<FormattedMessage id="voucher.alreadyEnrolled" />));
            break;
          case 'REGISTRATION_DATE_CLOSED':
            dispatch(showErrorNotification(<FormattedMessage id="trials.registrationEnd" />));
            break;
          case 'NO_GERMAN_INSTITUTE_VOUCHER_TRIAL':
            dispatch(showErrorNotification(<FormattedMessage id="trials.noGermanInstitute" />));
            break;
          default:
            dispatch(showErrorNotification(err.message));
        }
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };
  const onCloseAgbDialog = () => {
    showAGBDialog(false);
  };

  return (
    <>
      <VoucherDetail voucherDetail={voucherDetail}>
        <h3>
          <FormattedMessage id="voucher.finalizeMessage" />
        </h3>
        <Box sx={{ py: '20px', textAlign: 'left' }}>
          <FormControl required component="fieldset">
            <span>
              <FormattedMessage id="voucher.acceptTerm1" />
              &nbsp;
              <Link href="#" onClick={() => showAGBDialog(true)}>
                <FormattedMessage id="voucher.acceptTerm2" />
              </Link>
              {!termsUrl && (
                <span>.</span>
              )}
              &nbsp;
              {termsUrl && (
                <span>
                  <FormattedMessage id="voucher.acceptModality1" />
                  &nbsp;
                  <Link href="#" onClick={() => setShowTerms(true)}>
                    <FormattedMessage id="voucher.acceptModality2" />
                  </Link>
                  {locale.toLowerCase() === 'en' && (
                    <span>
                      &nbsp;
                      <FormattedMessage id="voucher.acceptModality3" />
                    </span>
                  )}
                  .
                </span>
              )}
            </span>
            <FormControlLabel
              control={<Checkbox checked={agbAccept} onChange={handleChangeTerms} name="terms" />}
              label={(<FormattedMessage id="voucher.acceptEverything" />)}
            />
            {
              agbAcceptError
              && (
                <FormHelperText error>
                  <FormattedMessage id="registration.agbAccept" />
                </FormHelperText>
              )
            }
          </FormControl>
        </Box>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={enrollHandler}
          >
            <FormattedMessage id="trials.enroll" />
          </Button>
        </Box>
      </VoucherDetail>
      <GenericDialog isOpen={agbDialog} closeButtonLabel={<FormattedMessage id="genericClose" />} onClose={onCloseAgbDialog}>
        <ContentBox show title="Terms and Conditions" elevation={0}>
          <AGB locale={locale} />
        </ContentBox>
      </GenericDialog>
      <TermsConfirmationDialog
        isOpen={showTerms}
        fileUrl={termsUrl}
        onClose={() => setShowTerms(false)}
      />
    </>
  );
};

export default VoucherEnrollment;
